import { Drawer, Pagination, Table } from '@/components/commons';
import { DeleteContent } from '@/components/commons/DeleteContent';
import { ENDPOINT } from '@/services/endpoint';
import { IResponse, IResponseError } from '@/types/common';
import {
  convertRequest,
  handleErrorCommon,
  notificationMessage,
  notificationModal,
  NOTIFY_STATUS,
} from '@/utils';
import { ERROR, SUCCESS, SUCCESS_MESSAGE } from '@/utils/constants/messages';
import { yupResolver } from '@hookform/resolvers/yup';
import { Anchor, Button, Image, Input, TableColumnsType } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { FormProvider, useForm } from 'react-hook-form';
import { useMutation, useQuery } from 'react-query';
import { PartnerForm } from '../Drawers';
import { formSchemaFn } from '../Drawers/PartnerForm/formSchemaFn';
import { FORM_NAME } from '../Drawers/PartnerForm/constants';
import { PartnerService } from '@/services/partner';
import { ICreatePartnerRequest, IUpdatePartnerRequest } from '@/types/request';
import { ImageService } from '@/services/image';
import { IUploadImageRequest } from '@/types/request/upload';
import {
  IGetDetailPartnerResponse,
  IGetListPartnerResponse,
} from '@/types/response';
import moment from 'moment';

interface IProps {
  title?: string;
}

interface IParams {
  companyName?: string;
  limit: number;
  page: number;
}

export const PartnerList: React.FC<IProps> = ({ title }) => {
  const [currentId, setCurrentId] = useState<number | null>(null);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [detail, setDetail] = useState<IGetDetailPartnerResponse>();
  const [params, setParams] = useState<IParams>({
    limit: 10,
    page: 1,
  });

  const { data, refetch, isFetching } = useQuery(
    [ENDPOINT.PARTNER.GET_LIST, params],
    () => PartnerService.getList(convertRequest(params)),
  );

  const methods = useForm<any>({
    mode: 'onChange',
    resolver: yupResolver(formSchemaFn(!!currentId)),
    reValidateMode: 'onChange',
  });

  const { handleSubmit, reset, setValue, setError } = methods;

  const handleCancel = () => {
    setOpenDrawer(false);
    setCurrentId(null);
    setDetail(undefined);
    reset({});
  };

  const handleCreate = async (values: any) => {
    const { attachments, profileAttachments, url1, url2, url3, ...rest } =
      values;
    const urlsArray = [url1, url2, url3].filter(
      (url) => url && typeof url === 'string' && url.trim() !== '',
    );
    const payload: ICreatePartnerRequest = {
      ...rest,
      coverImage: null,
      profileImage: null,
      urls: urlsArray.length > 0 ? urlsArray : null,
      attachments: null,
      profileAttachments: null,
    };
    try {
      if (values?.attachments?.fileList?.length) {
        const response = await ImageService.uploadFile(values?.attachments?.fileList[0]?.originFileObj);
        payload.coverImage = response?.data?.url;
      }
      if (values?.profileAttachments?.fileList?.length) {
        const response = await ImageService.uploadFile(values?.profileAttachments?.fileList[0]?.originFileObj);
        payload.profileImage = response?.data?.url;
      }
      await createPartner(payload);
    } catch (error) {
      console.log({ error });
    }
  };

  const { mutate: createPartner, isLoading: isLoadingCreate } = useMutation(
    (body: ICreatePartnerRequest) => PartnerService.create(body),
    {
      onSuccess: () => {
        notificationMessage({
          message:
            SUCCESS_MESSAGE[
            SUCCESS.CREATE_PARTNER as keyof typeof SUCCESS_MESSAGE
            ],
          type: NOTIFY_STATUS.SUCCESS,
        });
        refetch();
        handleCancel();
        setParams({ ...params, page: 1 });
      },
      onError: (response: IResponseError) => {
        handleErrorCommon(response, setError);
      },
    },
  );

  const { mutateAsync: uploadFile, isLoading: isLoadingUpload } = useMutation(
    (file: File) => ImageService.uploadFile(file),
    {
      onError: (response: IResponseError) => {
        handleErrorCommon(response, setError);
      },
    },
  );

  const { isFetching: isFetchingDetail } = useQuery(
    [ENDPOINT.PARTNER.GET_DETAIL.replace(':id', String(currentId))],
    () => PartnerService.getDetail(currentId!),
    {
      enabled: !!currentId,
      onSuccess: (response: IResponse<IGetDetailPartnerResponse>) => {
        const data = response?.data;
        setDetail(data);
        const urlsArray = Array.isArray(data?.urls)
          ? data.urls.filter(
            (url): url is string =>
              typeof url === 'string' && url.trim() !== '',
          )
          : [];
        const payload: any = {
          companyName: data?.companyName,
          description: data?.description,
          coverImage: data?.coverImage?.url,
          profileImage: data?.profileImage?.url,
          url1: urlsArray[0] || '',
          url2: urlsArray[1] || '',
          url3: urlsArray[2] || '',
          attachments: null,
          profileAttachments: null,
        };
        reset({
          formName: FORM_NAME.UPDATE_PARTNER,
          ...payload,
        });
      },
      onError: (error: IResponseError) => {
        handleErrorCommon(error, setError);
      },
    },
  );

  const handleUpdate = async (values: any) => {
    const { attachments, profileAttachments, url1, url2, url3, ...rest } =
      values;
    const urlsArray = [url1, url2, url3].filter(
      (url) => url && typeof url === 'string' && url.trim() !== '',
    );
    const payload: IUpdatePartnerRequest = {
      ...rest,
      coverImage: null,
      profileImage: null,
      urls: urlsArray.length > 0 ? urlsArray : null,
    };
    try {
      delete payload.attachments;
      delete payload.profileAttachments;
      delete payload.formName;

      if (values?.attachments?.fileList?.length) {
        const response = await uploadFile(values?.attachments?.fileList[0]?.originFileObj);
        payload.coverImage = response?.data?.url;
      } else if (values?.coverImage) {
        payload.coverImage = values.coverImage;
      }

      if (values?.profileAttachments?.fileList?.length) {
        const response = await uploadFile(values?.profileAttachments?.fileList[0]?.originFileObj);
        payload.profileImage = response?.data?.url;
      } else if (values?.profileImage) {
        payload.profileImage = values.profileImage;
      }

      await updatePartner({ body: payload, id: Number(currentId) });
    } catch (error) {
      console.log({ error });
    }
  };

  const handleDelete = (data: IGetDetailPartnerResponse) => {
    notificationModal({
      type: NOTIFY_STATUS.CONFIRM,
      title: '削除しますか？',
      content: <DeleteContent name='企業名' content={data?.companyName} />,
      maskClosable: false,
      onOk: () => deletePartner(currentId!),
      okText: '削除する',
      cancelText: 'キャンセル',
    });
  };

  const { mutate: updatePartner, isLoading: isLoadingUpdate } = useMutation(
    (payload: { body: IUpdatePartnerRequest; id: number }) =>
      PartnerService.update(payload.body, payload.id),
    {
      onSuccess: () => {
        notificationMessage({
          message:
            SUCCESS_MESSAGE[
            SUCCESS.UPDATE_PARTNER as keyof typeof SUCCESS_MESSAGE
            ],
          type: NOTIFY_STATUS.SUCCESS,
        });
        refetch();
        handleCancel();
        setParams({ ...params, page: 1 });
      },
      onError: (error: IResponseError) => {
        handleErrorCommon(error, setError);

        if (error.error === ERROR.PARTNER_NOT_FOUND) {
          setOpenDrawer(false);
          refetch();
        }
      },
    },
  );

  const onChangePagination = (page: number, pageSize: number) => {
    setParams({ ...params, page, limit: pageSize });
  };

  const { mutate: deletePartner, isLoading: isLoadingDelete } = useMutation(
    (id: number) => PartnerService.delete(id),
    {
      onSuccess: () => {
        notificationMessage({
          message:
            SUCCESS_MESSAGE[
            SUCCESS.DELETE_PARTNER as keyof typeof SUCCESS_MESSAGE
            ],
          type: NOTIFY_STATUS.SUCCESS,
        });
        refetch();
        handleCancel();
        setParams({ ...params, page: 1 });
      },
      onError: (error: IResponseError) => {
        handleErrorCommon(error, setError);

        if (error.error === ERROR.PARTNER_RELOAD_DATA) {
          setOpenDrawer(false);
          refetch();
        }
      },
    },
  );

  const columns: TableColumnsType<IGetListPartnerResponse> = [
    {
      title: 'ID',
      key: 'id',
      dataIndex: 'id',
      width: '5%',
      align: 'center',
    },
    {
      key: 'companyName',
      title: '企業名',
      align: 'left',
      dataIndex: 'companyName',
    },
    // {
    //   title: '説明文章',
    //   dataIndex: 'description',
    //   render: (value) => {
    //     return value?.length > 50 ? `${value.substring(0, 50)}...` : value;
    //   },
    // },
    // {
    //   title: 'カバー画像',
    //   dataIndex: 'coverImage',
    //   align: 'center',
    //   render: (value) =>
    //     value && <Image width={112} src={value?.url} preview={false} />,
    // },
    {
      title: 'プロフィール画像',
      dataIndex: 'profileImage',
      align: 'center',
      width: '20%',
      render: (value) =>
        value && <Image width={112} src={value?.url} preview={false} />,
    },
    // {
    //   title: '遷移先',
    //   dataIndex: 'urls',
    //   align: 'center',
    //   render: (value) =>
    //     value?.length > 0 &&
    //     value.map((url: string, index: number) => (
    //       <Anchor
    //         key={`${url}-${index}`}
    //         items={[{ href: url, target: '_blank', title: url, key: url }]}
    //       />
    //     )),
    // },
    {
      title: '登録日時',
      dataIndex: 'createdAt',
      align: 'center',
      width: '20%',
      render: (value) =>
        value ? moment(value).format('YYYY-MM-DD HH:mm:ss') : '-',
    },
  ];

  return (
    <div>
      <Helmet>
        <title>{title}</title>
      </Helmet>
      <div className='flex flex-col gap-[16px] mx-auto w-full max-w-[1280px] h-full'>
        <div className='flex justify-between'>
          <div className='flex justify-start gap-[6px] items-center'>
            <Input
              className='w-[300px]'
              prefix={<SearchOutlined className='text-[20px]' />}
              placeholder='企業名'
              onChange={(e) => {
                setParams({ ...params, page: 1, companyName: e.target.value });
              }}
            />
          </div>
          <header className='text-right'>
            <Button
              type='primary'
              onClick={() => {
                setOpenDrawer(true);
                reset({});
              }}
            >
              企業プロフィール新規作成
            </Button>
          </header>
        </div>

        <div className='flex justify-between flex-col'>
          <Table
            loading={isFetching || isLoadingUpload}
            scroll={{ x: 800 }}
            bordered
            rowKey={(record) => record.id}
            columns={columns}
            dataSource={data?.data.data}
            pagination={false}
            onRow={(record) => ({
              onClick: () => {
                setOpenDrawer(true);
                setCurrentId(record.id);
              },
            })}
            locale={{
              emptyText: 'パートナーはまだありません',
            }}
          />

          {data?.data?.pagination?.total! > 10 && (
            <Pagination
              className='pagination-table'
              total={data?.data.pagination.total}
              pageSize={params.limit}
              current={params.page}
              showSizeChanger={false}
              onChange={onChangePagination}
            />
          )}
        </div>
      </div>

      <Drawer
        title={!currentId ? '企業プロフィール新規作成' : '企業プロフィール詳細'}
        onOk={handleSubmit(currentId ? handleUpdate : handleCreate)}
        onCancel={handleCancel}
        onDelete={() => handleDelete(detail!)}
        maskClosable={false}
        open={openDrawer}
        okText={!currentId ? '保存 ' : '更新 '}
        loading={
          isLoadingUpdate ||
          isLoadingDelete ||
          isFetchingDetail ||
          isLoadingCreate ||
          isLoadingUpload
        }
        isDeleted={!!currentId}
      >
        <FormProvider {...methods}>
          <PartnerForm
            isLoading={
              isFetchingDetail ||
              isLoadingUpdate ||
              isLoadingCreate ||
              isLoadingDelete
            }
            openDrawer={openDrawer}
          />
        </FormProvider>
      </Drawer>
    </div>
  );
};
